import { Box, Paper, Typography } from "@mui/material";
import AppButton from "../../Common/Button/Button";
import InfoField from "../../Common/InfoField/InfoField";
import {
  getNotificationTemplateTypeLabel,
  getPushCategoryOptionsForType,
  notificationSeverityOptions,
  notificationTemplateTypes,
  pushNotificationTypeOptions,
} from "../notificationConstant";
import { getOptionLabel, normalizeTemplateContent } from "../notificationFormUtils";
import { emailSectionCardSx } from "../emailFormStyles";
import { format } from "date-fns";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return format(date, "dd MMM yyyy 'at' hh:mm a");
};

const HtmlBlock = ({ html }) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      p: 1.5,
      maxHeight: 240,
      overflow: "auto",
      "& img": { maxWidth: "100%" },
    }}
    dangerouslySetInnerHTML={{ __html: html || "-" }}
  />
);

const TemplateView = ({ template, onClose }) => {
  const type = template?.notificationType;
  const content = normalizeTemplateContent(type, template?.content);
  const imageUrl = (content.image || "").trim();
  const ctas = Array.isArray(content.ctas) ? content.ctas : [];
  const categoryOptions = getPushCategoryOptionsForType(content.type);

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Paper sx={emailSectionCardSx}>
        <InfoField label="Template Name" value={template?.templateName || "-"} />
        <InfoField
          label="Notification Type"
          value={getNotificationTemplateTypeLabel(type)}
        />
        <InfoField label="Last Updated" value={formatDate(template?.lastUpdated)} />
      </Paper>

      <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
        Content
      </Typography>
      <Paper sx={emailSectionCardSx}>
        {type === notificationTemplateTypes.push.value && (
          <>
            <InfoField
              label="Severity"
              value={getOptionLabel(notificationSeverityOptions, content.severity, "-")}
            />
            <InfoField
              label="Type"
              value={getOptionLabel(pushNotificationTypeOptions, content.type, content.type || "-")}
            />
            <InfoField
              label="Category"
              value={getOptionLabel(categoryOptions, content.category, content.category || "-")}
            />
            <InfoField label="Title" value={content.title || "-"} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Description
            </Typography>
            <HtmlBlock html={content.description} />
            <Box sx={{ mt: 1.5 }}>
              <InfoField label="Target Screen" value={content.targetScreen || "-"} />
            </Box>
            {imageUrl ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                  Image URL
                </Typography>
                <Box
                  component="img"
                  src={imageUrl}
                  alt="Template"
                  sx={{
                    maxWidth: 280,
                    maxHeight: 160,
                    objectFit: "contain",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.75, overflowWrap: "anywhere" }}
                >
                  {imageUrl}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mt: 2 }}>
                <InfoField label="Image URL" value="-" />
              </Box>
            )}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                CTA
              </Typography>
              {ctas.length ? (
                ctas.map((cta, index) => (
                  <Typography key={`${cta.label}-${index}`} variant="body2" sx={{ mb: 0.5 }}>
                    {cta.label || "-"} → {cta.target || "-"}
                  </Typography>
                ))
              ) : (
                <Typography variant="body2">-</Typography>
              )}
            </Box>
          </>
        )}

        {type === notificationTemplateTypes.email.value && (
          <>
            <InfoField label="Subject" value={content.subject || "-"} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Body
            </Typography>
            <HtmlBlock html={content.html} />
          </>
        )}

        {type === notificationTemplateTypes.sms.value && (
          <InfoField label="Message" value={content.body || "-"} />
        )}
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.5 }}>
        <AppButton variant="ghost" size="medium" onClick={onClose}>
          Close
        </AppButton>
      </Box>
    </Box>
  );
};

export default TemplateView;
